import { useOutletContext } from 'react-router-dom';
import { Construction } from 'lucide-react';

interface MatterDetail {
  id: string;
  matter_id: string | null;
  visa_subclass: string;
  variant: string | null;
}

interface PlaceholderProps {
  tabName: string;
  description: string;
  comingNext: string[];
}

export default function MatterTabPlaceholder({ tabName, description, comingNext }: PlaceholderProps) {
  const { matter } = useOutletContext<{ matter: MatterDetail }>();
  return (
    <div className="card p-8 text-center max-w-2xl mx-auto">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-50 text-amber-600 mb-4">
        <Construction size={20} />
      </div>
      <h2 className="text-lg font-bold text-[#0B2341] mb-2">{tabName} tab - coming next</h2>
      <p className="text-sm text-slate-600 mb-4">{description}</p>
      <div className="text-left bg-slate-50 rounded-lg p-4 max-w-md mx-auto">
        <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
          On this matter
        </div>
        <div className="text-sm text-[#0B2341]">
          <span className="font-mono">{matter.matter_id ?? '-'}</span>
          <span className="text-slate-400 mx-2">·</span>
          <span>
            {matter.visa_subclass}
            {matter.variant ? ` ${matter.variant}` : ''}
          </span>
        </div>
      </div>
      {comingNext.length > 0 && (
        <div className="mt-4 text-left max-w-md mx-auto">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
            What this tab will do
          </div>
          <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
            {comingNext.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Per-tab thin wrappers so App.tsx routes are clean.
export function NotesTab() {
  return (
    <MatterTabPlaceholder
      tabName="Notes"
      description="Internal-only file notes (MARA reg. 5). Markdown supported."
      comingNext={[
        'Add file note with author, timestamp, and "file note" badge',
        'Link notes to specific events (RFI reply, doc upload)',
        'RLS-enforced: clients never see this',
      ]}
    />
  );
}

export function TasksTab() {
  return (
    <MatterTabPlaceholder
      tabName="Tasks"
      description="The kanban cards for this matter only. Compact list view."
      comingNext={[
        'Per-matter slice of the staff kanban (separate scope)',
        'Inline state changes (drag or dropdown)',
        '"Add task" for ad-hoc work like chasing a witness',
      ]}
    />
  );
}

export function TimelineTab() {
  return (
    <MatterTabPlaceholder
      tabName="Timeline"
      description="Reverse-chronological feed of audit log + state changes + comms."
      comingNext={[
        'Filter by event type',
        'Expand each entry for full payload',
        'CSV export for OMARA audits',
      ]}
    />
  );
}

export function LodgementTab() {
  return (
    <MatterTabPlaceholder
      tabName="Lodgement"
      description="Pre-lodgement checks, approval gate, TRN entry, post-lodgement state."
      comingNext={[
        'Pre-flight checks: engagement letter, intake submitted, docs complete',
        'Approval state + history (Eli-only)',
        'TRN + immi.gov.au confirmation upload',
        'Post-lodgement: lodgement date, BVA status, Medicare note',
      ]}
    />
  );
}
